/** Normalize catalog strings consistently across builders (matches MyCourses `collapseSpaces`). */
export function collapseCourseDisplayName(s) {
  return String(s ?? '').trim().replace(/\s+/g, ' ');
}

function idLessThan(a, b) {
  return Number(a) < Number(b);
}

/**
 * `/api/courses` may return duplicate rows (same logical course_name, different ids).
 * Dropdowns should show each course once; prefer the lowest numeric id per normalized name.
 */
/** Normalize `students.courses` from API (TEXT[], JSON string, or legacy shapes). */
export function normalizeStudentCoursesArray(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map((c) => String(c).trim()).filter(Boolean);
  }
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) {
        return parsed.map((c) => String(c).trim()).filter(Boolean);
      }
    } catch {
      /* fall through */
    }
    if (s.startsWith('{') && s.endsWith('}')) {
      return s
        .slice(1, -1)
        .split(',')
        .map((t) => t.replace(/^"|"$/g, '').trim())
        .filter(Boolean);
    }
    return [s];
  }
  return [];
}

/** Map profile course strings to canonical catalog names (case-insensitive). */
export function alignStudentCoursesToCatalog(studentCourses, catalogNames) {
  const byLower = new Map();
  catalogNames.forEach((name) => {
    const n = collapseCourseDisplayName(name);
    if (n) byLower.set(n.toLowerCase(), n);
  });
  const out = [];
  const seen = new Set();
  normalizeStudentCoursesArray(studentCourses).forEach((raw) => {
    const key = collapseCourseDisplayName(raw).toLowerCase();
    const canonical = byLower.get(key) || collapseCourseDisplayName(raw);
    if (!canonical || seen.has(canonical.toLowerCase())) return;
    seen.add(canonical.toLowerCase());
    out.push(canonical);
  });
  return out.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

export function dedupeCoursesForDropdown(list) {
  if (!Array.isArray(list)) return [];
  const byId = new Map();
  for (const c of list) {
    if (c == null || c.id == null) continue;
    const id = Number(c.id);
    if (Number.isNaN(id)) continue;
    if (!byId.has(id)) byId.set(id, c);
  }
  const byNormName = new Map();
  for (const c of byId.values()) {
    const name = collapseCourseDisplayName(c.course_name);
    if (!name) continue;
    const key = name.toLowerCase();
    const prev = byNormName.get(key);
    if (!prev || idLessThan(c.id, prev.id)) byNormName.set(key, c);
  }
  return Array.from(byNormName.values()).sort((a, b) =>
    collapseCourseDisplayName(a.course_name).localeCompare(
      collapseCourseDisplayName(b.course_name),
      undefined,
      { sensitivity: 'base' }
    )
  );
}
